import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Megaphone,
  Plus,
  Users,
  X,
} from "lucide-react";
import api from "../../services/api";
import { useToast } from "../../contexts/ToastContext";

const STATUS = [
  ["not_evaluated", "Não avaliado"],
  ["organizing", "Em organização"],
  ["ready", "Pronto para apresentar"],
  ["not_applicable", "Não se aplica"],
  ["needs_guidance", "Requer orientação"],
];
const ROLES = {
  asb: "ASB",
  tsb: "TSB",
  tpd: "TPD",
  intern: "Estagiário",
  other: "Outro profissional",
};
const RELATIONSHIPS = {
  employee: "Empregado",
  contractor: "Prestador",
  internship: "Estágio",
  partner: "Sócio",
  other: "Outro",
};
const EMPTY = {
  name: "",
  role_type: "asb",
  cro_number: "",
  cro_state: "",
  relationship_type: "employee",
  started_at: "",
  ended_at: "",
  institution: "",
  supervisor: "",
  agreement_reference: "",
  registration_verified: false,
  relationship_verified: false,
  documents_verified: false,
  notes: "",
  active: true,
};
const asDate = (value) => (value ? String(value).slice(0, 10) : "");

export default function TeamAdvertisingPanel({ canManage }) {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [team, setTeam] = useState(null);
  const [ads, setAds] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [initial, setInitial] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initial),
    [form, initial],
  );

  const load = async () => {
    try {
      const [teamResponse, adsResponse] = await Promise.all([
        api.get("/compliance/inspection-team"),
        api.get("/compliance/advertising-review"),
      ]);
      setTeam(teamResponse.data);
      setAds(adsResponse.data);
    } catch (error) {
      showToast(
        error?.response?.data?.detail ||
          "Não foi possível carregar equipe e publicidade.",
        "error",
      );
    }
  };
  useEffect(() => {
    load();
  }, []);

  const saveDentist = async (item, changes) => {
    try {
      await api.put(`/compliance/inspection-team/${item.professional_id}`, {
        cro_verified: item.cro_verified,
        relationship_verified: item.relationship_verified,
        specialties_verified: item.specialties_verified,
        notes: item.notes || null,
        ...changes,
      });
      await load();
    } catch (error) {
      showToast(
        error?.response?.data?.detail ||
          "Não foi possível atualizar o dentista.",
        "error",
      );
    }
  };
  const showMember = (item = null) => {
    const value = item
      ? {
          ...EMPTY,
          ...item,
          started_at: asDate(item.started_at),
          ended_at: asDate(item.ended_at),
        }
      : { ...EMPTY };
    setEditing(item);
    setForm(value);
    setInitial(value);
    setModalOpen(true);
  };
  const closeMember = () => {
    if (dirty && !window.confirm("Descartar as alterações deste cadastro?"))
      return;
    setModalOpen(false);
    setEditing(null);
  };
  const saveMember = async (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      cro_number: form.cro_number || null,
      cro_state: form.cro_state ? form.cro_state.toUpperCase() : null,
      relationship_type: form.relationship_type || null,
      started_at: form.started_at ? `${form.started_at}T00:00:00` : null,
      ended_at: form.ended_at ? `${form.ended_at}T00:00:00` : null,
      institution: form.institution || null,
      supervisor: form.supervisor || null,
      agreement_reference: form.agreement_reference || null,
      notes: form.notes || null,
    };
    try {
      if (editing?.id)
        await api.put(
          `/compliance/inspection-team/support/${editing.id}`,
          payload,
        );
      else await api.post("/compliance/inspection-team/support", payload);
      setInitial(payload);
      setModalOpen(false);
      setEditing(null);
      await load();
      showToast("Cadastro da equipe atualizado.", "success");
    } catch (error) {
      showToast(
        error?.response?.data?.detail ||
          "Não foi possível salvar o integrante.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };
  const upload = async (item, file) => {
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    try {
      await api.post(
        `/compliance/inspection-team/support/${item.id}/document`,
        data,
      );
      await load();
      showToast("Documento anexado.", "success");
    } catch (error) {
      showToast(
        error?.response?.data?.detail || "Não foi possível anexar o documento.",
        "error",
      );
    }
  };
  const openDocument = async (item) => {
    try {
      const response = await api.get(
        `/compliance/inspection-team/support/${item.id}/document`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.document_original_filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showToast(
        error?.response?.data?.detail || "Não foi possível abrir o documento.",
        "error",
      );
    }
  };
  const saveAd = async (item, status) => {
    try {
      const response = await api.patch(
        `/compliance/advertising-review/${item.id}`,
        { status, note: item.note || null },
      );
      setAds(response.data);
    } catch (error) {
      showToast(
        error?.response?.data?.detail ||
          "Não foi possível atualizar a revisão.",
        "error",
      );
    }
  };
  if (!team || !ads) return null;

  return (
    <section className="rounded-2xl bg-white shadow-sm">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between p-5 text-left sm:p-6"
      >
        <div>
          <h3 className="font-semibold text-gray-900">Equipe e publicidade</h3>
          <p className="mt-1 text-sm text-gray-500">
            Organize dentistas, equipe auxiliar, estágio e divulgação.
          </p>
        </div>
        {open ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {open && (
        <div className="space-y-7 border-t p-5 sm:p-6">
          <div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-700" />
              <h4 className="font-semibold text-gray-900">
                Cirurgiões-dentistas da clínica
              </h4>
              <span className="ml-auto text-xs text-gray-500">
                {team.dentists_ready}/{team.dentists_total} revisados
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Vêm de Configurações e continuam disponíveis para agenda e
              documentos clínicos.
            </p>
            {!team.dentists_total ? (
              <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                Cadastre os cirurgiões-dentistas em Configurações da clínica.
              </p>
            ) : (
              <div className="mt-3 divide-y rounded-xl border">
                {team.professionals.map((item) => (
                  <div key={item.professional_id} className="p-4">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      Cirurgião-dentista ·{" "}
                      {item.cro_number
                        ? `CRO-${item.cro_state || ""} ${item.cro_number}`
                        : "CRO não informado"}
                      {item.specialties.length
                        ? ` · ${item.specialties.join(", ")}`
                        : ""}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600">
                      {[
                        ["cro_verified", "Registro conferido"],
                        ["relationship_verified", "Vínculo conferido"],
                        ["specialties_verified", "Títulos conferidos"],
                      ].map(([key, label]) => (
                        <label key={key} className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            disabled={!canManage}
                            checked={Boolean(item[key])}
                            onChange={(event) =>
                              saveDentist(item, { [key]: event.target.checked })
                            }
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Users className="h-5 w-5 text-indigo-700" />
              <h4 className="font-semibold text-gray-900">
                Equipe auxiliar e estágio
              </h4>
              <span className="text-xs text-gray-500">
                {team.support_ready}/{team.support_total} revisados
              </span>
              {canManage && (
                <button
                  onClick={() => showMember()}
                  className="ml-auto inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white"
                >
                  <Plus className="h-4 w-4" />
                  Adicionar integrante
                </button>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Cadastro documental para ASB, TSB, TPD, estagiários e outros. Não
              cria login, agenda ou acesso.
            </p>
            {!team.support_team.length ? (
              <p className="mt-3 rounded-lg border border-dashed p-4 text-sm text-gray-500">
                Nenhum integrante auxiliar cadastrado.
              </p>
            ) : (
              <div className="mt-3 divide-y rounded-xl border">
                {team.support_team.map((item) => (
                  <div
                    key={item.id}
                    className={`p-4 ${!item.active ? "opacity-60" : ""}`}
                  >
                    <div className="flex flex-wrap justify-between gap-2">
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          {ROLES[item.role_type]}
                          {item.cro_number
                            ? ` · CRO-${item.cro_state || ""} ${item.cro_number}`
                            : ""}
                          {!item.active ? " · Inativo" : ""}
                        </p>
                      </div>
                      {canManage && (
                        <button
                          onClick={() => showMember(item)}
                          className="text-xs font-medium text-indigo-700"
                        >
                          Editar
                        </button>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-600">
                      <span>
                        {item.registration_verified
                          ? "✓ Registro/formação"
                          : "○ Registro/formação"}
                      </span>
                      <span>
                        {item.relationship_verified ? "✓ Vínculo" : "○ Vínculo"}
                      </span>
                      <span>
                        {item.documents_verified
                          ? "✓ Documentos"
                          : "○ Documentos"}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs">
                      {item.document_original_filename && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-indigo-700"
                          onClick={() => openDocument(item)}
                        >
                          <FileText className="h-4 w-4" />
                          {item.document_original_filename}
                        </button>
                      )}
                      {canManage && (
                        <label className="cursor-pointer text-indigo-700">
                          {item.document_original_filename
                            ? "Substituir documento"
                            : "Anexar documento"}
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                            onChange={(event) =>
                              upload(item, event.target.files?.[0])
                            }
                          />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-indigo-700" />
              <h4 className="font-semibold text-gray-900">
                Revisão de publicidade
              </h4>
              <span className="ml-auto text-xs text-gray-500">
                {ads.progress}%
              </span>
            </div>
            <div className="mt-3 divide-y rounded-xl border">
              {ads.items.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col justify-between gap-3 p-4 sm:flex-row sm:items-start"
                >
                  <div>
                    <p className="font-medium text-gray-900">{item.title}</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {item.description}
                    </p>
                  </div>
                  {canManage ? (
                    <select
                      value={item.status}
                      onChange={(event) => saveAd(item, event.target.value)}
                      className="shrink-0 rounded-lg border p-2 text-xs"
                    >
                      {STATUS.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs text-gray-500">
                      {STATUS.find(([value]) => value === item.status)?.[1]}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-500">{ads.disclaimer}</p>
          </div>
        </div>
      )}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeMember();
          }}
        >
          <form
            onSubmit={saveMember}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">
                  {editing ? "Editar integrante" : "Adicionar integrante"}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Cadastro exclusivo para organização documental de
                  fiscalização.
                </p>
              </div>
              <button type="button" onClick={closeMember}>
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <label className="text-sm">
                Nome
                <input
                  required
                  minLength={2}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full rounded-lg border p-2"
                />
              </label>
              <label className="text-sm">
                Função
                <select
                  value={form.role_type}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      role_type: e.target.value,
                      relationship_type:
                        e.target.value === "intern"
                          ? "internship"
                          : form.relationship_type,
                    })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                >
                  {Object.entries(ROLES).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              {form.role_type !== "intern" && (
                <>
                  <label className="text-sm">
                    Número do CRO (se aplicável)
                    <input
                      value={form.cro_number}
                      onChange={(e) =>
                        setForm({ ...form, cro_number: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border p-2"
                    />
                  </label>
                  <label className="text-sm">
                    UF do CRO
                    <input
                      maxLength={2}
                      value={form.cro_state}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          cro_state: e.target.value.toUpperCase(),
                        })
                      }
                      className="mt-1 w-full rounded-lg border p-2"
                    />
                  </label>
                </>
              )}
              <label className="text-sm">
                Tipo de vínculo
                <select
                  value={form.relationship_type || ""}
                  onChange={(e) =>
                    setForm({ ...form, relationship_type: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                >
                  <option value="">Não informado</option>
                  {Object.entries(RELATIONSHIPS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                Início do vínculo
                <input
                  type="date"
                  value={form.started_at}
                  onChange={(e) =>
                    setForm({ ...form, started_at: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                />
              </label>
              {form.role_type === "intern" && (
                <>
                  <label className="text-sm">
                    Instituição de ensino
                    <input
                      value={form.institution}
                      onChange={(e) =>
                        setForm({ ...form, institution: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border p-2"
                    />
                  </label>
                  <label className="text-sm">
                    Supervisor responsável
                    <input
                      value={form.supervisor}
                      onChange={(e) =>
                        setForm({ ...form, supervisor: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border p-2"
                    />
                  </label>
                  <label className="text-sm sm:col-span-2">
                    Referência do convênio/termo
                    <input
                      value={form.agreement_reference}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          agreement_reference: e.target.value,
                        })
                      }
                      className="mt-1 w-full rounded-lg border p-2"
                    />
                  </label>
                </>
              )}
              <label className="text-sm">
                Término do vínculo (opcional)
                <input
                  type="date"
                  value={form.ended_at}
                  onChange={(e) =>
                    setForm({ ...form, ended_at: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                />
              </label>
              <label className="flex items-center gap-2 self-end pb-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) =>
                    setForm({ ...form, active: e.target.checked })
                  }
                />
                Integrante ativo
              </label>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 rounded-lg bg-gray-50 p-3 text-sm">
              {[
                [
                  "registration_verified",
                  form.role_type === "intern"
                    ? "Formação/estágio conferido"
                    : "Registro/formação conferido",
                ],
                ["relationship_verified", "Vínculo conferido"],
                ["documents_verified", "Documentos conferidos"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form[key]}
                    onChange={(e) =>
                      setForm({ ...form, [key]: e.target.checked })
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            <label className="mt-4 block text-sm">
              Observações
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1 w-full rounded-lg border p-2"
              />
            </label>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeMember}
                className="rounded-lg border px-4 py-2 text-sm"
              >
                Cancelar
              </button>
              <button
                disabled={saving}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {saving ? "Salvando..." : "Salvar integrante"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
